import {
  createPolicyItemConfigurationResolver,
  type InsuranceProductPolicyItemConfiguration,
  type InsuranceProductPolicyItemConfigurationResolver,
} from '../../../model/policy-item-configuration.ts'
import type { PolicyItems } from '../../../model/policy-item.ts'
import type { FrMrpaxePolicyItem, FrMrpaxePolicyItemsState } from '../fr-mrpaxe-insurance.ts'

import { frMrpaxeDeductibleByPolicyItem_v1_0_0 as deductibles } from './deductible-by-policy-item.ts'
import { frMrpaxeLimitOfIndemnityByPolicyItem_v1_0_0 as limits } from './limit-of-indemnity-by-policy-item.ts'

const shownIncluded: InsuranceProductPolicyItemConfiguration = {
  isHidden: false,
  canBeUpdated: false,
  canBeSelected: false,
}

const shownSelectable: InsuranceProductPolicyItemConfiguration = {
  isHidden: false,
  canBeUpdated: false,
  canBeSelected: true,
}

const hiddenFromCustomer: InsuranceProductPolicyItemConfiguration = {
  isHidden: true,
  canBeUpdated: false,
  canBeSelected: false,
}

const included: InsuranceProductPolicyItemConfigurationResolver<FrMrpaxePolicyItemsState> = () => shownIncluded
const selectable: InsuranceProductPolicyItemConfigurationResolver<FrMrpaxePolicyItemsState> = () => shownSelectable
/** The customer is not offered the works liability and the boost: only the back office takes them. */
const selectableInBackoffice = createPolicyItemConfigurationResolver<FrMrpaxePolicyItemsState>({
  selfOnboardingConfiguration: () => hiddenFromCustomer,
  backofficeConfiguration: () => shownSelectable,
})

/**
 * The twelve guarantees, the options and the assistance. The cover of the preserved goods
 * and the professional liability depends on the activity: the trigger writes `excluded` where the activity does not
 * have it.
 */
export const frMrpaxePolicyItems_v1_0_0 = {
  Incendie: {
    name: 'Incendie',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.Incendie.select,
    selectDeductible: deductibles.Incendie.select,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.Incendie.additionalInfo,
      deductible: deductibles.Incendie.additionalInfo,
    },
  },
  DommagesElec: {
    name: 'DommagesElec',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.DommagesElec.select,
    selectDeductible: deductibles.DommagesElec.select,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.DommagesElec.additionalInfo,
      deductible: deductibles.DommagesElec.additionalInfo,
    },
  },
  DegatsEaux: {
    name: 'DegatsEaux',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.DegatsEaux.select,
    selectDeductible: deductibles.DegatsEaux.select,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.DegatsEaux.additionalInfo,
      deductible: deductibles.DegatsEaux.additionalInfo,
    },
  },
  Vol: {
    name: 'Vol',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.Vol.select,
    selectDeductible: deductibles.Vol.select,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.Vol.additionalInfo,
      deductible: deductibles.Vol.additionalInfo,
    },
  },
  Vandalisme: {
    name: 'Vandalisme',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.Vandalisme.select,
    selectDeductible: deductibles.Vandalisme.select,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.Vandalisme.additionalInfo,
      deductible: deductibles.Vandalisme.additionalInfo,
    },
  },
  EvenementsClim: {
    name: 'EvenementsClim',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.EvenementsClim.select,
    selectDeductible: deductibles.EvenementsClim.select,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.EvenementsClim.additionalInfo,
      deductible: deductibles.EvenementsClim.additionalInfo,
    },
  },
  CatastropheNat: {
    name: 'CatastropheNat',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.CatastropheNat.select,
    possibleDeductibles: deductibles.CatastropheNat.deductible,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.CatastropheNat.additionalInfo,
      deductible: deductibles.CatastropheNat.additionalInfo,
    },
  },
  Attentat: {
    name: 'Attentat',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.Attentat.select,
    selectDeductible: deductibles.Attentat.select,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.Attentat.additionalInfo,
      deductible: deductibles.Attentat.additionalInfo,
    },
  },
  MarchConservee: {
    name: 'MarchConservee',
    type: 'guarantee',
    optionality: ['included', 'excluded'],
    selectLimitOfIndemnity: limits.MarchConservee.select,
    selectDeductible: deductibles.MarchConservee.select,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.MarchConservee.additionalInfo,
      deductible: deductibles.MarchConservee.additionalInfo,
    },
  },
  BrisMateriel: {
    name: 'BrisMateriel',
    type: 'guarantee',
    optionality: ['included'],
    selectLimitOfIndemnity: limits.BrisMateriel.select,
    selectDeductible: deductibles.BrisMateriel.select,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.BrisMateriel.additionalInfo,
      deductible: deductibles.BrisMateriel.additionalInfo,
    },
  },
  RespCivOccupant: {
    name: 'RespCivOccupant',
    type: 'guarantee',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.RespCivOccupant.limit,
    selectDeductible: deductibles.RespCivOccupant.select,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.RespCivOccupant.additionalInfo,
      deductible: deductibles.RespCivOccupant.additionalInfo,
    },
  },
  publicLiability: {
    name: 'publicLiability',
    type: 'guarantee',
    optionality: ['included', 'excluded'],
    possibleLimitOfIndemnities: limits.publicLiability.limit,
    selectDeductible: deductibles.publicLiability.select,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.publicLiability.additionalInfo,
      deductible: deductibles.publicLiability.additionalInfo,
    },
  },
  PE: {
    name: 'PE',
    type: 'guarantee',
    optionality: ['optional'],
    selectLimitOfIndemnity: limits.PE.select,
    possibleDeductibles: deductibles.PE.deductible,
    dependsOn: [],
    editConfiguration: selectable,
    additionalInfo: {
      limitOfIndemnity: limits.PE.additionalInfo,
      deductible: deductibles.PE.additionalInfo,
    },
  },
  BDG: {
    name: 'BDG',
    type: 'guarantee',
    optionality: ['optional'],
    selectLimitOfIndemnity: limits.BDG.select,
    possibleDeductibles: deductibles.BDG.deductible,
    dependsOn: [],
    editConfiguration: selectable,
    additionalInfo: {
      limitOfIndemnity: limits.BDG.additionalInfo,
      deductible: deductibles.BDG.additionalInfo,
    },
  },
  PVV: {
    name: 'PVV',
    type: 'guarantee',
    optionality: ['optional'],
    selectLimitOfIndemnity: limits.PVV.select,
    selectDeductible: deductibles.PVV.select,
    dependsOn: [],
    editConfiguration: selectable,
    additionalInfo: {
      limitOfIndemnity: limits.PVV.additionalInfo,
      deductible: deductibles.PVV.additionalInfo,
    },
  },
  RCT: {
    name: 'RCT',
    type: 'guarantee',
    optionality: ['optional'],
    possibleLimitOfIndemnities: limits.RCT.limit,
    selectDeductible: deductibles.RCT.select,
    dependsOn: [],
    editConfiguration: selectableInBackoffice,
    additionalInfo: {
      limitOfIndemnity: limits.RCT.additionalInfo,
      deductible: deductibles.RCT.additionalInfo,
    },
  },
  BOOST: {
    name: 'BOOST',
    type: 'improvement',
    optionality: ['optional'],
    selectLimitOfIndemnity: limits.BOOST.select,
    possibleDeductibles: deductibles.BOOST.deductible,
    dependsOn: [],
    editConfiguration: selectableInBackoffice,
    additionalInfo: {
      limitOfIndemnity: limits.BOOST.additionalInfo,
      deductible: deductibles.BOOST.additionalInfo,
    },
  },
  assistance: {
    name: 'assistance',
    type: 'service',
    optionality: ['included'],
    possibleLimitOfIndemnities: limits.assistance.limit,
    possibleDeductibles: deductibles.assistance.deductible,
    dependsOn: [],
    editConfiguration: included,
    additionalInfo: {
      limitOfIndemnity: limits.assistance.additionalInfo,
      deductible: deductibles.assistance.additionalInfo,
    },
  },
} as const satisfies PolicyItems<FrMrpaxePolicyItem, FrMrpaxePolicyItemsState>
